import type { ReactNode } from 'react';
import type { IntelligenceSummary } from '~/common/types';
import type { ExportableColumnDef } from '~/components2/Table';
import { Cell, getSummaryValue, pgeCell, renderHeaderWithTooltip, toMiles, YesNoCell } from './common';
import sceData from './sceInfoConfig';

type Def = ExportableColumnDef<IntelligenceSummary>;

export const pgeRegionAndDivision: Def[] = [
  {
    id: 'division',
    size: 150,
    header: 'Division',
    accessorFn: (s) => s.pge?.division || '',
    cell: ({ getValue }) => pgeCell(getValue()),
  },
  {
    id: 'region',
    size: 150,
    header: 'Region',
    accessorFn: (s) => s.pge?.region || '',
    cell: ({ getValue }) => pgeCell(getValue()),
  },
];

export const pgeGasInformation: Def[] = [
  {
    id: 'gasInformation',
    header: 'Gas Information',
    columns: [
      {
        id: 'gasServiceCount',
        header: 'Gas Service Count',
        accessorFn: (summary) => getSummaryValue({ summary, value: summary.gasServiceCount }),
        cell: (cell) => {
          return <Cell value={cell.getValue<number | string>()?.toLocaleString() || ''} summary={cell.row.original} />;
        },
      },
      {
        id: 'gasDistributionLength',
        header: 'Gas Distribution Length (mi.)',
        accessorFn: (summary) =>
          getSummaryValue({
            summary,
            value: toMiles(summary.gasDistributionLength || 0),
          }),
        cell: (cell) => {
          return <Cell value={cell.getValue<number | string>()?.toLocaleString() || ''} summary={cell.row.original} />;
        },
      },
    ],
  },
];

type PermittingAgencyField = 'incorporatedPlaces' | 'counties' | 'landUseTransportationDistrictBoundaries';

const joinNames = (s: IntelligenceSummary, field: PermittingAgencyField) => {
  // Null until the intelligence compute populates the field.
  return (s[field] ?? []).map((p) => p.name).join(', ');
};

const yesOrNoForSomeFields = (
  s: IntelligenceSummary,
  fields: (keyof IntelligenceSummary)[],
  yesText: string = 'Yes',
) => {
  const isYes = fields.some((field) => s[field] === true);
  if (isYes) return yesText;

  const allNo = fields.every((field) => s[field] === false);
  if (allNo) return 'No';

  return '';
};

const someHaveLength = (s: IntelligenceSummary, fields: (PermittingAgencyField | 'environmentalFederalLands')[]) => {
  return fields.some((field) => !!s[field]?.length);
};

const someAreTrue = (s: IntelligenceSummary, fields: (keyof IntelligenceSummary)[]) => {
  return fields.some((field) => s[field] === true);
};

const riskScores = {
  localPermitting: 1,
  environmental: 2,
  governmentLands: 3,
  coastalCommission: 4,
  caltrans: 5,
  dwr: 6,
  nativeLands: 7,
  landAcquisition: 8,
} as const;

type RiskScore = keyof typeof riskScores;

const riskScoreLabels: Partial<{ [key in RiskScore]: string }> = {
  localPermitting: 'Local Permitting',
  environmental: 'Environmental',
  governmentLands: 'Government Lands',
  coastalCommission: 'Local Coastal Commission',
  caltrans: 'CalTrans',
  // dwr: 'DWR',
  nativeLands: 'Native/Tribal Lands',
  landAcquisition: 'Land Acquisition (ROE/Easement)',
} as const;

const getRiskScore = (s: IntelligenceSummary) => {
  let score = 0;

  // local permitting agencies
  if (someHaveLength(s, ['incorporatedPlaces', 'counties', 'landUseTransportationDistrictBoundaries'])) {
    score += riskScores.localPermitting;
  }

  // environmental
  if (
    someAreTrue(s, [
      'exclusionProtectedAreaDatabaseHoldingsCPADPresent',
      'exclusionUSGSProtectedAreasPresent',
      'environmentalWetlandsPresent',
      'exclusionLandHeldInConservationEasementCCEDPresent',
      'environmentalHazardousWastePresent',
      'environmentalGeoTrackerSitesPresent',
    ])
  ) {
    score += riskScores.environmental;
  }

  // government lands
  if (
    someHaveLength(s, [
      'environmentalFederalLands',
      // state parks
    ]) ||
    someAreTrue(s, ['californiaStateParkBoundariesPresent'])
  ) {
    score += riskScores.governmentLands;
  }

  // coastal commission
  if (someAreTrue(s, ['environmentalLocalCoastalProgramPresent'])) {
    score += riskScores.coastalCommission;
  }

  if (someAreTrue(s, ['landUseRoadsStateHighwayPresent'])) {
    score += riskScores.caltrans;
  }

  // add DWR here (RS 6)

  if (someAreTrue(s, ['federallyRecognizedTribalLandsPresent'])) {
    score += riskScores.nativeLands;
  }

  if (s.landRightsParcelsCount) {
    score += riskScores.landAcquisition;
  }

  return score;
};

const RiskScore = ({ score }: { score: number }) => {
  return (
    <div>
      <span className="font-semibold">Risk Score: </span>
      {score}
    </div>
  );
};

const getSceInfoColumn = (summary: IntelligenceSummary, key: string) => {
  const tdNumber = summary.project.name.split(' ')[0]?.replace('TD', '');
  const config = sceData[tdNumber];
  return config?.[key] ?? '';
};

/** SCE header tooltip content by column id, shared with the new Conflict Summary table. */
export const sceHeaderTooltips: Record<string, ReactNode> = {
  localPermitting: (
    <div>
      <div>All cities, counties, and CalTrans districts in the project area.</div>
      <RiskScore score={riskScores.localPermitting} />
    </div>
  ),
  environmental: (
    <div>
      <div>Indicates whether the project intersects with an environmentally sensitive area.</div>

      <RiskScore score={riskScores.environmental} />
    </div>
  ),
  restrictedLands: (
    <div>
      <div>Does the project intersect with either:</div>
      <ul className="list-inside list-disc pl-2">
        <li>CPAD Protected Area Database Holdings</li>
        <li>USGS Protected Areas</li>
      </ul>
    </div>
  ),
  wetlands: (
    <div>
      <div>Does the project intersect with a wetland(s)?</div>
    </div>
  ),
  conservationLands: (
    <div>
      <div>Does the project intersect with a land held in conservation easement?</div>
    </div>
  ),
  envirostor: (
    <div>
      <div>Is the project within 100' of a DTSC EnviroStor site?</div>
    </div>
  ),
  geotracker: (
    <div>
      <div>Is the project within 100' of a SWRCB GeoTracker site?</div>
    </div>
  ),
  governmentLands: (
    <div>
      <div>Federal, state, and local government lands.</div>
      <RiskScore score={riskScores.governmentLands} />
    </div>
  ),
  californiaStateParkBoundaries: (
    <div>
      <div>Does the project intersect with a California state park?</div>
    </div>
  ),
  federalLands: <div>Federal lands intersecting the project.</div>,
  federallyRecognizedTribalLands: (
    <div>
      <div>Does the project intersect with any federally recognized tribal lands?</div>
      <RiskScore score={riskScores.nativeLands} />
    </div>
  ),
  caltrans: (
    <div>
      <div>Does the project intersect with a CalTrans-owned R/W?</div>
      <RiskScore score={riskScores.caltrans} />
    </div>
  ),
  coastalCommission: (
    <div>
      <div>Does the project intersect with a Local Coastal Commission?</div>
      <RiskScore score={riskScores.coastalCommission} />
    </div>
  ),
  landAcquisition: (
    <div>
      <div>The number of parcels that intersect the project.</div>
      <RiskScore score={riskScores.landAcquisition} />
    </div>
  ),
  riskScore: (
    <div>
      <div>The risk score for the project.</div>
      <div>The risk score is calculated as the sum of all project risk rankings:</div>

      <ul className="list-inside list-disc pl-2">
        {Object.entries(riskScoreLabels).map(([key, label]) => (
          <li key={key}>
            <span className="font-semibold">{label}:</span> {riskScores[key as RiskScore]}
          </li>
        ))}
      </ul>
    </div>
  ),
};

export const sceColumns: Def[] = [
  {
    id: 'program',
    header: 'Program',
    accessorFn: (s) => getSceInfoColumn(s, 'Program'),
  },
  {
    id: 'pif',
    header: 'PIF',
    accessorFn: (s) => getSceInfoColumn(s, 'PIF'),
  },
  // don't need this for now since its in the project name
  // {
  //   id: 'tdNumber',
  //   header: 'TD',
  //   accessorFn: (s) => getSceInfoColumn(s, 'TD'),
  // },
  {
    id: 'circuit',
    header: 'Circuit',
    accessorFn: (s) => getSceInfoColumn(s, 'Circuit'),
  },
  {
    id: 'leader',
    header: 'Leader',
    accessorFn: (s) => getSceInfoColumn(s, 'Leader'),
  },
  {
    id: 'region',
    header: 'Region',
    accessorFn: (s) => getSceInfoColumn(s, 'Region'),
  },
  {
    id: 'workType',
    header: 'Work Type',
    accessorFn: (s) => getSceInfoColumn(s, 'Work Type'),
  },
  {
    id: 'ugMiles',
    header: 'UG Miles',
    accessorFn: (s) => getSceInfoColumn(s, 'UG Miles'),
  },
  {
    id: 'ohDeMiles',
    header: 'OH DE Miles',
    accessorFn: (s) => getSceInfoColumn(s, 'OH DE Miles'),
  },
  {
    id: 'localPermitting',
    exportLabel: 'Local Permitting',
    header: () => renderHeaderWithTooltip('Local Permitting', sceHeaderTooltips.localPermitting),
    columns: [
      {
        id: 'city',
        header: 'Cities',
        accessorFn: (s) => joinNames(s, 'incorporatedPlaces'),
      },
      {
        id: 'county',
        header: 'Counties',
        accessorFn: (s) => joinNames(s, 'counties'),
      },
      {
        id: 'stateDOT',
        header: 'CalTrans District(s) No.',
        accessorFn: (s) => joinNames(s, 'landUseTransportationDistrictBoundaries'),
      },
    ],
  },
  {
    id: 'environmental',
    exportLabel: 'Environmental',
    header: () => renderHeaderWithTooltip('Environmental', sceHeaderTooltips.environmental),
    columns: [
      {
        id: 'restrictedLands',
        exportLabel: 'Restricted Lands',
        header: () => renderHeaderWithTooltip('Restricted Lands', sceHeaderTooltips.restrictedLands),
        accessorFn: (s) =>
          yesOrNoForSomeFields(s, [
            'exclusionProtectedAreaDatabaseHoldingsCPADPresent',
            'exclusionUSGSProtectedAreasPresent',
          ]),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
      {
        id: 'wetlands',
        exportLabel: 'Wetlands',
        header: () => renderHeaderWithTooltip('Wetlands', sceHeaderTooltips.wetlands),
        accessorFn: (s) => yesOrNoForSomeFields(s, ['environmentalWetlandsPresent']),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
      {
        id: 'conservationLands',
        exportLabel: 'CCED Lands Held in Conservation Easement',
        header: () =>
          renderHeaderWithTooltip('CCED Lands Held in Conservation Easement', sceHeaderTooltips.conservationLands),
        accessorFn: (s) => yesOrNoForSomeFields(s, ['exclusionLandHeldInConservationEasementCCEDPresent']),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
      {
        id: 'envirostor',
        exportLabel: 'DTSC EnviroStor Sites',
        header: () => renderHeaderWithTooltip('DTSC EnviroStor Sites', sceHeaderTooltips.envirostor),
        accessorFn: (s) => yesOrNoForSomeFields(s, ['environmentalHazardousWastePresent'], "Within 100'"),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
      {
        id: 'geotracker',
        exportLabel: 'SWRCB GeoTracker Sites',
        header: () => renderHeaderWithTooltip('SWRCB GeoTracker Sites', sceHeaderTooltips.geotracker),
        accessorFn: (s) => yesOrNoForSomeFields(s, ['environmentalGeoTrackerSitesPresent'], "Within 100'"),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
    ],
  },
  {
    id: 'governmentLands',
    exportLabel: 'Government Lands',
    header: () => renderHeaderWithTooltip('Government Lands', sceHeaderTooltips.governmentLands),
    columns: [
      {
        id: 'californiaStateParkBoundaries',
        exportLabel: 'California State Parks',
        header: () =>
          renderHeaderWithTooltip('California State Parks', sceHeaderTooltips.californiaStateParkBoundaries),
        accessorFn: (s) => yesOrNoForSomeFields(s, ['californiaStateParkBoundariesPresent']),
        cell: ({ getValue }) => <YesNoCell value={getValue()} />,
      },
      {
        id: 'federalLands',
        exportLabel: 'Federal Lands',
        header: () => renderHeaderWithTooltip('Federal Lands', sceHeaderTooltips.federalLands),
        // Either name part can be absent; never print a literal 'undefined'.
        accessorFn: (s) =>
          s.environmentalFederalLands
            ?.map((f) => [f.agencyName, f.unitName].filter(Boolean).join(' - '))
            .filter(Boolean)
            .join(', ') ?? '',
      },
    ],
  },
  {
    id: 'federallyRecognizedTribalLands',
    exportLabel: 'Federally Recognized Tribal Lands',
    header: () =>
      renderHeaderWithTooltip('Federally Recognized Tribal Lands', sceHeaderTooltips.federallyRecognizedTribalLands),
    accessorFn: (s) => yesOrNoForSomeFields(s, ['federallyRecognizedTribalLandsPresent']),
    cell: ({ getValue }) => <YesNoCell value={getValue()} />,
  },
  {
    id: 'caltrans',
    exportLabel: 'CalTrans',
    header: () => renderHeaderWithTooltip('CalTrans', sceHeaderTooltips.caltrans),
    accessorFn: (s) => yesOrNoForSomeFields(s, ['landUseRoadsStateHighwayPresent']),
    cell: ({ getValue }) => <YesNoCell value={getValue()} />,
  },
  {
    id: 'coastalCommission',
    exportLabel: 'Local Coastal Commission',
    header: () => renderHeaderWithTooltip('Local Coastal Commission', sceHeaderTooltips.coastalCommission),
    accessorFn: (s) => yesOrNoForSomeFields(s, ['environmentalLocalCoastalProgramPresent']),
    cell: ({ getValue }) => <YesNoCell value={getValue()} />,
  },
  {
    id: 'landAcquisition',
    exportLabel: 'Land Acquisition (ROE/Easement)',
    header: () => renderHeaderWithTooltip('Land Acquisition', sceHeaderTooltips.landAcquisition),
    accessorFn: (s) => s.landRightsParcelsCount ?? '',
  },
  {
    id: 'riskScore',
    exportLabel: 'Risk Score',
    header: () => renderHeaderWithTooltip('Risk Score', sceHeaderTooltips.riskScore),
    accessorFn: getRiskScore,
  },
];
